import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import { useToastContext } from '../../contexts/ToastContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import useSubscriptionStatus from '../../hooks/useSubscriptionStatus';
import { useAvailablePlans, getVendorStatus, subscribe } from '../../services/subscriptionService';
import PaymentModal from '../../components/shared/PaymentModal';
import {
  normalizeBillingChoice,
  planIsFree,
  planMaxTierPrice,
  planMonthlyPrice,
  planOffersBothIntervals,
  planYearlyPrice,
  priceForBillingChoice,
  sortPlansForDisplay,
  resolvePopularPaidPlanSlug,
  planTierLabel,
  planTierSubtitle,
} from '../../utils/subscriptionPlanPricing';
import {
  Check,
  X,
  Calendar,
  Users,
  Image,
  BarChart3,
  Palette,
  Headphones,
  Code,
  Clock,
  MapPin,
  Star,
  AlertCircle,
  ArrowLeft,
  Sparkles,
  Package,
} from 'lucide-react';

const VendorSubscription = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { success, error: showError } = useToastContext();
  const { checkSubscriptionStatus } = useSubscription();
  const { hasActiveSubscription, isChecking } = useSubscriptionStatus();
  const navigate = useNavigate();

  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [subscribingToFree, setSubscribingToFree] = useState(false);
  const [globalBilling, setGlobalBilling] = useState('monthly');

  const { data: subscriptionsData, isLoading: subscriptionsLoading, error: subscriptionsError } =
    useAvailablePlans();

  const subscriptions = useMemo(() => subscriptionsData?.data || [], [subscriptionsData?.data]);

  const sortedPlans = useMemo(() => sortPlansForDisplay(subscriptions), [subscriptions]);
  const popularPaidSlug = useMemo(() => resolvePopularPaidPlanSlug(sortedPlans), [sortedPlans]);
  const showGlobalBillingToggle = useMemo(
    () => sortedPlans.some((p) => planOffersBothIntervals(p)),
    [sortedPlans]
  );

  const gridColsClass = useMemo(() => {
    const n = sortedPlans.length;
    if (n >= 4) return 'grid-cols-1 md:grid-cols-2 xl:grid-cols-4';
    if (n === 3) return 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3';
    return 'grid-cols-1 md:grid-cols-2';
  }, [sortedPlans.length]);

  const { data: vendorSubPayload } = useQuery({
    queryKey: ['vendor-subscription-status'],
    queryFn: async () => {
      const r = await getVendorStatus();
      return r?.data ?? r;
    },
    enabled: user?.user_type === 'vendor',
    staleTime: 60 * 1000,
  });

  const currentPlan = useMemo(() => {
    const id = vendorSubPayload?.current_subscription?.subscription_plan_id;
    if (!id || !subscriptions.length) return null;
    return subscriptions.find((p) => p.id === id) || null;
  }, [vendorSubPayload, subscriptions]);

  const isFreeTierActive = Boolean(vendorSubPayload?.current_subscription?.is_free_tier);

  const comparePlanTier = (current, plan) => {
    const so = Number(plan.sort_order) - Number(current.sort_order);
    if (so !== 0) return so;
    return planMaxTierPrice(plan) - planMaxTierPrice(current);
  };

  const canStillUpgrade = useMemo(() => {
    if (!currentPlan || !subscriptions.length) return true;
    const maxSort = Math.max(...subscriptions.map((p) => Number(p.sort_order ?? 0)));
    const isFree = planIsFree(currentPlan);
    return isFree || Number(currentPlan.sort_order) < maxSort;
  }, [currentPlan, subscriptions]);

  useEffect(() => {
    if (user?.user_type !== 'vendor') {
      navigate('/');
      return;
    }
    if (isChecking || subscriptionsLoading || hasActiveSubscription === null) return;
    if (hasActiveSubscription !== true || !subscriptions.length) return;

    const timer = setTimeout(async () => {
      try {
        const response = await getVendorStatus();
        const sub = response?.data?.current_subscription;
        if (!sub) return;
        const current = subscriptions.find((p) => p.id === sub.subscription_plan_id);
        if (!current) return;

        const maxSort = Math.max(...subscriptions.map((p) => Number(p.sort_order ?? 0)));
        const isFree = planIsFree(current);
        const canUpgrade = isFree || Number(current.sort_order) < maxSort;

        if (canUpgrade) return;

        const daysRemaining = sub.days_remaining;
        if (daysRemaining != null && daysRemaining <= 5) return;

        navigate('/vendor', { replace: true });
      } catch (e) {
        console.error(e);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [
    user?.user_type,
    hasActiveSubscription,
    isChecking,
    subscriptionsLoading,
    subscriptions,
    navigate,
  ]);

  const handleSelectPlan = async (plan) => {
    if (currentPlan) {
      if (plan.id === currentPlan.id) {
        showError('Current plan', 'You are already on this plan.');
        return;
      }
      if (comparePlanTier(currentPlan, plan) < 0) {
        showError(
          'Lower plan',
          'Downgrading is not available here. Contact support if you need to change to a lower tier.'
        );
        return;
      }
    }

    const isFree = planIsFree(plan);
    if (isFree) {
      try {
        setSubscribingToFree(true);
        const response = await subscribe({
          subscription_id: plan.id,
          payment_method: 'free',
          billing_interval: 'monthly',
        });
        if (response.success) {
          handlePaymentSuccess(response);
        } else {
          showError(response.message || 'Failed to subscribe to free plan');
        }
      } catch (err) {
        showError(err?.response?.data?.message || err?.message || 'Failed to subscribe to free plan');
      } finally {
        setSubscribingToFree(false);
      }
    } else {
      setSelectedPlan(plan);
      setShowPaymentModal(true);
    }
  };

  const handlePaymentSuccess = (data) => {
    const msg =
      data?.message ||
      (data?.subscription ? 'Your plan has been updated.' : 'Successfully subscribed! Welcome to your new plan.');
    success('Subscription', msg);

    checkSubscriptionStatus();
    queryClient.invalidateQueries({ queryKey: ['vendor-plan-access'] });
    queryClient.invalidateQueries({ queryKey: ['vendor-subscription-status'] });

    setShowPaymentModal(false);

    setTimeout(() => {
      navigate('/vendor', { replace: true });
    }, 1000);
  };

  const handlePaymentError = (error) => {
    showError(error || 'Payment failed. Please try again.');
  };

  const getFeatureIcon = (feature) => {
    const icons = {
      analytics: BarChart3,
      custom_branding: Palette,
      priority_support: Headphones,
      api_access: Code,
      advanced_booking: Clock,
      multi_location: MapPin,
    };
    return icons[feature] || Check;
  };

  const renderFeature = (feature, value, label) => {
    const Icon = getFeatureIcon(feature);
    return (
      <div className="flex items-center space-x-2">
        {value ? (
          <Check className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-green-500 flex-shrink-0" />
        ) : (
          <X className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-400 flex-shrink-0" />
        )}
        <Icon className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-500 flex-shrink-0" />
        <span className="text-xs sm:text-sm text-gray-600">{label}</span>
      </div>
    );
  };

  const planCardShell = (plan) => {
    if (planIsFree(plan)) {
      return 'relative rounded-xl sm:rounded-2xl overflow-hidden border-2 border-dashed border-gray-300 bg-gradient-to-b from-gray-50 via-white to-white shadow-md';
    }
    if (plan.slug === popularPaidSlug) {
      return 'relative rounded-xl sm:rounded-2xl overflow-hidden bg-white shadow-xl border-l-4 border-primary-500 ring-2 ring-primary-500/90 md:scale-[1.02]';
    }
    return 'relative rounded-xl sm:rounded-2xl overflow-hidden bg-white border border-gray-200 shadow-lg';
  };

  const planButtonClass = (plan) => {
    if (currentPlan && plan.id === currentPlan.id) {
      return 'bg-gray-200 text-gray-600 cursor-not-allowed';
    }
    if (currentPlan && comparePlanTier(currentPlan, plan) < 0) {
      return 'bg-gray-100 text-gray-500 cursor-not-allowed';
    }
    if (planIsFree(plan)) {
      return 'border-2 border-gray-900 text-gray-900 bg-white hover:bg-gray-50';
    }
    if (plan.slug === popularPaidSlug) {
      return 'bg-primary-600 text-white hover:bg-primary-700';
    }
    return 'bg-gray-900 text-white hover:bg-gray-800';
  };

  useEffect(() => {
    if (subscriptionsError) {
      showError(subscriptionsError.message || 'Failed to load subscription plans');
    }
  }, [subscriptionsError, showError]);

  if (isChecking || subscriptionsLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">
            {isChecking ? 'Checking subscription status...' : 'Loading subscription plans...'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-6 sm:py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-6 sm:mb-10">
          <div className="mb-4 sm:mb-6">
            <button
              type="button"
              onClick={() => navigate('/')}
              className="inline-flex items-center px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
              Back to Site
            </button>
          </div>

          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-2 sm:mb-4">
            {hasActiveSubscription && canStillUpgrade ? 'Upgrade your subscription' : 'Choose Your Subscription Plan'}
          </h1>
          <p className="text-base sm:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto px-2">
            {hasActiveSubscription && canStillUpgrade
              ? 'Each tier is built for a different stage — compare limits and premium tools, then pick what fits.'
              : 'Compare tiers below. New vendors start on Free with core access; upgrade when you need more.'}
          </p>
        </div>

        {hasActiveSubscription && currentPlan && (
          <div
            className={`mb-6 max-w-2xl mx-auto rounded-lg px-4 py-3 text-center text-sm ${
              isFreeTierActive
                ? 'border border-amber-200 bg-amber-50 text-amber-950'
                : 'border border-primary-200 bg-primary-50 text-primary-900'
            }`}
          >
            You are on <strong>{currentPlan.name}</strong>
            {isFreeTierActive && canStillUpgrade ? (
              <>
                {' '}
                — many premium pages stay locked until you upgrade.{' '}
                <span className="font-medium">Pick a paid tier below</span> for branding, analytics, and more.
              </>
            ) : canStillUpgrade ? (
              <> — choose a higher plan below to upgrade.</>
            ) : (
              '.'
            )}
          </div>
        )}

        {hasActiveSubscription === false && (
          <div className="mb-4 sm:mb-8">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 sm:p-4 flex items-start sm:items-center space-x-2 sm:space-x-3">
              <AlertCircle className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-600 flex-shrink-0 mt-0.5 sm:mt-0" />
              <div>
                <h3 className="text-xs sm:text-sm font-medium text-yellow-800">Activate your workspace</h3>
                <p className="text-xs sm:text-sm text-yellow-700 mt-1">
                  No subscription is on file yet — choose Free or a paid plan below. New accounts are usually assigned Free automatically; refresh or pick a plan if you still see this.
                </p>
              </div>
            </div>
          </div>
        )}

        {showGlobalBillingToggle && (
          <div className="flex flex-col items-center mb-8">
            <p className="text-sm font-medium text-gray-700 mb-3">Billing period for paid plans</p>
            <div
              role="tablist"
              aria-label="Billing period for paid plans"
              className="inline-flex rounded-full bg-gray-100 p-1 border border-gray-200 shadow-sm"
            >
              {[
                { value: 'monthly', label: 'Monthly' },
                { value: 'yearly', label: 'Yearly' },
              ].map(({ value, label }) => {
                const isActive = globalBilling === value;
                return (
                  <button
                    key={value}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    onClick={() => setGlobalBilling(value)}
                    className={`min-w-[7.5rem] px-5 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                      isActive
                        ? 'bg-white text-primary-700 shadow-sm ring-1 ring-black/5'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
            {globalBilling === 'yearly' && (
              <p className="mt-2 text-xs text-gray-500 text-center max-w-sm">
                Only plans with a yearly price are billed annually
              </p>
            )}
          </div>
        )}

        <div className={`grid ${gridColsClass} gap-4 sm:gap-6 lg:gap-8 mb-6 sm:mb-12`}>
          {sortedPlans.map((plan) => (
            <div key={plan.id} className={planCardShell(plan)}>
              {plan.slug === popularPaidSlug && !planIsFree(plan) && (
                <div className="absolute top-0 right-0 bg-primary-500 text-white px-2 sm:px-4 py-1 text-xs sm:text-sm font-medium rounded-bl-lg z-10">
                  <Star className="h-3 w-3 sm:h-4 sm:w-4 inline mr-1" />
                  Popular
                </div>
              )}

              <div className="p-4 sm:p-6 lg:p-8">
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  {planIsFree(plan) && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-gray-800 px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-white">
                      <Sparkles className="h-3 w-3" />
                      {planTierLabel(plan, popularPaidSlug)}
                    </span>
                  )}
                  {!planIsFree(plan) && (
                    <span
                      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold uppercase tracking-wide ${
                        plan.slug === popularPaidSlug
                          ? 'bg-primary-100 text-primary-900'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {planTierLabel(plan, popularPaidSlug)}
                    </span>
                  )}
                </div>

                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1">{plan.name}</h3>
                <p className="text-sm text-gray-700 mb-4 leading-relaxed">{planTierSubtitle(plan)}</p>

                <div className="mb-5">
                  <span className="text-3xl sm:text-4xl font-bold text-gray-900">
                    {planIsFree(plan)
                      ? 'Free'
                      : `$${priceForBillingChoice(plan, globalBilling).toFixed(2)}`}
                  </span>
                  {!planIsFree(plan) && (
                    <span className="text-sm sm:text-base text-gray-600 ml-1">
                      /{normalizeBillingChoice(plan, globalBilling) === 'yearly' ? 'year' : 'month'}
                    </span>
                  )}
                  {planIsFree(plan) && (
                    <span className="block text-xs text-gray-500 mt-1">Renews monthly · upgrade anytime</span>
                  )}
                  {!planIsFree(plan) && planOffersBothIntervals(plan) && (
                    <span className="block text-xs text-gray-500 mt-1">
                      {globalBilling === 'yearly'
                        ? `Yearly $${planYearlyPrice(plan).toFixed(2)} · Monthly $${planMonthlyPrice(plan).toFixed(2)}/mo`
                        : `Monthly $${planMonthlyPrice(plan).toFixed(2)} · Yearly $${planYearlyPrice(plan).toFixed(2)}/yr`}
                    </span>
                  )}
                </div>

                <button
                  type="button"
                  onClick={() => handleSelectPlan(plan)}
                  disabled={
                    subscribingToFree ||
                    (currentPlan &&
                      (plan.id === currentPlan.id || comparePlanTier(currentPlan, plan) < 0))
                  }
                  className={`w-full py-2.5 sm:py-3 px-4 rounded-lg text-sm sm:text-base font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${planButtonClass(plan)}`}
                >
                  {subscribingToFree ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></span>
                      Subscribing...
                    </span>
                  ) : currentPlan && plan.id === currentPlan.id ? (
                    'Current plan'
                  ) : currentPlan && comparePlanTier(currentPlan, plan) < 0 ? (
                    'Lower tier'
                  ) : currentPlan && comparePlanTier(currentPlan, plan) > 0 ? (
                    `Upgrade to ${plan.name}`
                  ) : planIsFree(plan) ? (
                    `Start on ${plan.name}`
                  ) : (
                    `Choose ${plan.name}`
                  )}
                </button>
              </div>

              <div
                className={`border-t p-4 sm:p-6 lg:p-8 ${
                  planIsFree(plan) ? 'border-gray-200 bg-gray-50/60' : 'border-gray-200 bg-gray-50/30'
                }`}
              >
                <h4 className="text-xs font-bold uppercase tracking-wide text-gray-500 mb-3">
                  {planIsFree(plan) ? 'Starter toolkit' : 'Capacity'}
                </h4>
                <div className="space-y-2 sm:space-y-3 mb-5">
                  <div className="flex items-center space-x-2">
                    <Users className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-500 flex-shrink-0" />
                    <span className="text-xs sm:text-sm text-gray-700">
                      {plan.max_bookings_per_month
                        ? `Up to ${plan.max_bookings_per_month} bookings/month`
                        : 'Unlimited bookings'}
                    </span>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Image className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-500 flex-shrink-0" />
                    <span className="text-xs sm:text-sm text-gray-700">
                      {plan.max_services ? `Up to ${plan.max_services} services` : 'Unlimited services'}
                    </span>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Calendar className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-500 flex-shrink-0" />
                    <span className="text-xs sm:text-sm text-gray-700">
                      {plan.max_portfolio_items
                        ? `Up to ${plan.max_portfolio_items} portfolio items`
                        : 'Unlimited portfolio items'}
                    </span>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Package className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-500 flex-shrink-0" />
                    <span className="text-xs sm:text-sm text-gray-700">
                      {plan.max_packages_per_service != null
                        ? `Up to ${plan.max_packages_per_service} packages per service`
                        : 'Unlimited packages per service'}
                    </span>
                  </div>
                </div>

                <h4 className="text-xs font-bold uppercase tracking-wide text-gray-500 mb-3">
                  {planIsFree(plan) ? 'Locked until you upgrade' : 'Premium capabilities'}
                </h4>
                <div className="space-y-2 sm:space-y-3">
                  {renderFeature('analytics', plan.feature_analytics, 'Advanced Analytics')}
                  {renderFeature('custom_branding', plan.feature_custom_branding, 'Custom Branding')}
                  {renderFeature('priority_support', plan.feature_priority_support, 'Priority Support')}
                  {renderFeature('advanced_booking', plan.feature_advanced_booking, 'Advanced Booking')}
                  {renderFeature('multi_location', plan.feature_multi_location, 'Multi-Location')}
                </div>

                {Array.isArray(plan.features_list) && plan.features_list.length > 0 && (
                  <div className="mt-5 pt-4 border-t border-gray-200">
                    <p className="text-xs font-bold uppercase tracking-wide text-gray-500 mb-2">
                      Highlights
                    </p>
                    <ul className="space-y-1.5">
                      {plan.features_list.slice(0, 5).map((line, idx) => (
                        <li key={idx} className="flex gap-2 text-xs text-gray-600 leading-snug">
                          <span className="text-primary-600 font-bold mt-0.5">·</span>
                          <span>{line}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        <PaymentModal
          isOpen={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          selectedPlan={selectedPlan}
          billingInterval={
            selectedPlan ? normalizeBillingChoice(selectedPlan, globalBilling) : 'monthly'
          }
          checkoutAmount={
            selectedPlan ? priceForBillingChoice(selectedPlan, globalBilling) : 0
          }
          onPaymentSuccess={handlePaymentSuccess}
          onPaymentError={handlePaymentError}
        />
      </div>
    </div>
  );
};

export default VendorSubscription;
